/**
 * The Google Picker runs in a docs.google.com iframe that also needs the user's Google session.
 * When the browser blocks third-party cookies for this site (Brave Shields, Safari, Firefox's
 * strict mode), the frame shows Google's own "Sign in to your Google Account" page and the picker
 * never reports anything - no callback fires. A successful picker reports `loaded` (undocumented
 * but reliable) within a second or two, so no report after a few seconds means it's stuck there.
 * Short on purpose: a user staring at Google's sign-in page won't wait long, and if a slow picker
 * does report in after the warning, the warning is withdrawn.
 */
export const PICKER_LOAD_TIMEOUT_MS = 4_000;

export const pickerBlockedMessage = (host: string) =>
  'Google Drive didn\'t load. If Google is asking you to sign in although you already are, your browser ' +
  `is blocking third-party cookies for ${host}. Allow them for this site (in Brave, turn Shields down) ` +
  'and try again.';

/**
 * Starts watching a picker that was just shown. Pass every picker callback action to
 * `onAction`. `onStuck` runs once if none arrives within `timeoutMs`; `onRecovered` runs if one
 * arrives after that (the picker was just slow), so the warning can be withdrawn.
 */
export const watchPickerLoad = (
  onStuck: () => void,
  onRecovered: () => void = () => undefined,
  timeoutMs = PICKER_LOAD_TIMEOUT_MS
) => {
  let stuck = false;
  let timer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
    timer = undefined;
    stuck = true;
    onStuck();
  }, timeoutMs);

  const stop = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
  };

  // Any report at all (loaded, picked, cancel, error) proves the picker itself is running
  const onAction = () => {
    stop();
    if (stuck) {
      stuck = false;
      onRecovered();
    }
  };

  return { onAction, stop };
};
